import type { ComponentProps } from 'react';
import { Toolbar as Base } from '@base-ui/react/toolbar';
import { cn } from './cn';

/**
 * A row (or column) of controls with ONE tab stop: Tab enters and leaves the bar, the arrow keys move (and wrap)
 * between its controls (Base UI's toolbar, roving focus). Skins nothing: the bar's own classes come from the caller
 * (`FLOATING_BAR` for the editor's bars).
 *
 * A control joins the bar by being rendered through `Toolbar.Button`, whatever it is:
 *   <Toolbar.Button render={<IconButton label="Bold" icon={…} />} />
 *   <Toolbar.Button render={<Menu.Trigger aria-label="More" />}>…</Toolbar.Button>
 * Anything else in the bar (a label, a count) is not an item and keeps no tab stop.
 */
function Root({ label, orientation = 'horizontal', className, ...rest }: Omit<ComponentProps<typeof Base.Root>, 'aria-label'> & { label: string }) {
  return <Base.Root aria-label={label} orientation={orientation} className={className} {...rest} />;
}

/** The line between two groups of a bar; it turns with the bar (a vertical line in a row, a horizontal one in a column). */
function Separator({ className, ...rest }: ComponentProps<typeof Base.Separator>) {
  return (
    <Base.Separator
      className={cn(
        'shrink-0 self-stretch bg-border data-[orientation=vertical]:mx-1 data-[orientation=vertical]:my-1.5 data-[orientation=vertical]:w-px data-[orientation=horizontal]:mx-1.5 data-[orientation=horizontal]:my-1 data-[orientation=horizontal]:h-px',
        className as string,
      )}
      {...rest}
    />
  );
}

export const Toolbar = { Root, Button: Base.Button, Group: Base.Group, Separator, Link: Base.Link, Input: Base.Input };
